// node_modules
import { ChangeEvent, FC, useContext, useEffect, useRef, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot, faCalendarAlt, faFile, faBuilding, faGlobe } from "@fortawesome/free-solid-svg-icons";
// Enums
import { PatentDateTypeEnum, ScienceDocumentTypeEnum, SearchQueryTypeEnum } from "Enums";
// Components
import { DeletableTextbox, DivDropdown, FindestRefTextBox, FindestTextBox } from "Components/Shared";
// Contexts
import { SavedQueryContext, UserContext } from "Providers";
// Controllers
import { SavedQueryControllerSingleton } from "Controllers";
// styles
import styles from "./findDocumentsQueryFields.module.scss";

type TFindDocumentsQueryFieldsProps = {
    selectedSearchQueryType: SearchQueryTypeEnum,
    handleSearchQueryTypeChange: (newSearchQueryTypeEnum: SearchQueryTypeEnum) => void
}

const INVALID_DATE_MESSAGE = "Invalid Date";

export const FindDocumentsQueryFields: FC<TFindDocumentsQueryFieldsProps> = ({ selectedSearchQueryType,
        handleSearchQueryTypeChange }: TFindDocumentsQueryFieldsProps) => {

    // Contexts
    const { savedQuery, updateSavedQuery } = useContext(SavedQueryContext);
    const { isCurrentUserFindest } = useContext(UserContext);

    // Refs
    const newAffiliationTextboxRef = useRef<HTMLInputElement>(null);
    const newCountryTextboxRef = useRef<HTMLInputElement>(null);

    // Constants
    const technologyPredictionModels = ["general", "device", "ingredient", "material", "method", "process"]; //, "TEP (beta)"];
    const patentCountryOptions = ["none", "show only", "exclude"];
    const patentDateOptions = ["publication", "filing"];

    // Determine which machine learning model will be used for the prediction
    const determinePredictionModel = useCallback((): string => {
        const modelIndex = technologyPredictionModels.indexOf(savedQuery?.filters?.technologyFilters?.modelType ?? technologyPredictionModels[0]) ?? 0;

        if(modelIndex === -1) return technologyPredictionModels[0];
        return technologyPredictionModels[modelIndex];
    }, [savedQuery?.filters?.technologyFilters?.modelType, technologyPredictionModels]);

    // State
    const [selectedTechnologyPredictionModel, setSelectedTechnologyPredictionModel] = useState(determinePredictionModel());
    const [selectedCountryOptionIndex, setSelectedCountryOptionIndex] = useState(savedQuery?.filters?.patentFilters?.countryFilterType ?? 0);
    const [selectedPatentDateOption, setSelectedPatentDateOption] = useState(patentDateOptions[savedQuery?.filters?.patentFilters?.patentDateType === PatentDateTypeEnum.Filing ? 1 : 0 ?? 0]);

    useEffect(() => {
        setSelectedTechnologyPredictionModel(determinePredictionModel());
        setSelectedCountryOptionIndex(savedQuery?.filters?.patentFilters?.countryFilterType ?? 0);
        setSelectedPatentDateOption(patentDateOptions[savedQuery?.filters?.patentFilters?.patentDateType === PatentDateTypeEnum.Filing ? 1 : 0 ?? 0]);
    }, [determinePredictionModel, patentDateOptions, savedQuery, technologyPredictionModels]);

    // Handlers:
    // Affiliation Handlers
    const onAddAffiliation = async () => {
        if(!newAffiliationTextboxRef.current) return;
        if(!savedQuery || !savedQuery.filters) return;
        const newSavedQuery = savedQuery;

        if(selectedSearchQueryType === SearchQueryTypeEnum.ScienceArticle) {
            if(!newSavedQuery.filters.scienceFilters ||
                !newSavedQuery.filters.scienceFilters.affiliations) newSavedQuery.filters.scienceFilters.affiliations = [];
            newSavedQuery.filters.scienceFilters.affiliations = [...newSavedQuery.filters.scienceFilters.affiliations, newAffiliationTextboxRef.current.value];
        } else if(selectedSearchQueryType === SearchQueryTypeEnum.Patent) {
            if(!newSavedQuery.filters.patentFilters || 
                !newSavedQuery.filters.patentFilters.assignees) newSavedQuery.filters.patentFilters.assignees = [];
            newSavedQuery.filters.patentFilters.assignees = [...newSavedQuery.filters.patentFilters.assignees, newAffiliationTextboxRef.current.value];
        }

        updateSavedQuery(newSavedQuery);
        newAffiliationTextboxRef.current.value = "";
        await SavedQueryControllerSingleton.updateFilters(newSavedQuery);
    };

    const onChangeAffiliation = async (index: number, newText: string) => {
        if(!savedQuery || !savedQuery.filters) return;
        const newSavedQuery = savedQuery;
        if(selectedSearchQueryType === SearchQueryTypeEnum.ScienceArticle) {
            if(!newSavedQuery.filters.scienceFilters ||
                !newSavedQuery.filters.scienceFilters.affiliations) return;

            newSavedQuery.filters.scienceFilters.affiliations = newSavedQuery.filters.scienceFilters.affiliations.map((affiliation, currentIndex) => {
                if(currentIndex === index) {
                    return newText;
                }
                return affiliation;
            });
        } else if(selectedSearchQueryType === SearchQueryTypeEnum.Patent) {
            if(!newSavedQuery.filters.patentFilters ||
                !newSavedQuery.filters.patentFilters.assignees) return;

            newSavedQuery.filters.patentFilters.assignees = newSavedQuery.filters.patentFilters.assignees.map((assignee, currentIndex) => {
                if(currentIndex === index) {
                    return newText;
                }
                return assignee;
            });
        }

        updateSavedQuery(newSavedQuery);
        await SavedQueryControllerSingleton.updateFilters(newSavedQuery);
    };

    const onDeleteAffiliation = async (index: number) => {
        if(!savedQuery || !savedQuery.filters) return;
        const newSavedQuery = savedQuery;

        if(selectedSearchQueryType === SearchQueryTypeEnum.ScienceArticle) {
            if(!newSavedQuery.filters.scienceFilters ||
                !newSavedQuery.filters.scienceFilters.affiliations) return;

            newSavedQuery.filters.scienceFilters.affiliations = newSavedQuery.filters.scienceFilters.affiliations.filter((_, i) => i !== index);
        } else if(selectedSearchQueryType === SearchQueryTypeEnum.Patent) {
            if(!newSavedQuery.filters.patentFilters ||
                !newSavedQuery.filters.patentFilters.assignees) return;

            newSavedQuery.filters.patentFilters.assignees = newSavedQuery.filters.patentFilters.assignees.filter((_, i) => i !== index);
        }

        updateSavedQuery(newSavedQuery);
        await SavedQueryControllerSingleton.updateFilters(newSavedQuery);
    };

    // Science document type handler
    const onChangeScienceDocumentType = async (event: ChangeEvent<HTMLInputElement>) => {
        if(!savedQuery || !savedQuery.filters || !savedQuery.filters.scienceFilters) return;
        const newSavedQuery = savedQuery;
        const newDocumentTypesEnabled = { ...newSavedQuery.filters.scienceFilters.documentTypesEnabled };

        newDocumentTypesEnabled[parseInt(event.target.value)] = event.target.checked;
        newSavedQuery.filters.scienceFilters.documentTypesEnabled = newDocumentTypesEnabled; 

        updateSavedQuery(newSavedQuery);
        await SavedQueryControllerSingleton.updateFilters(newSavedQuery);
    };

    // Date handlers
    const onPatentDateTimeChange = async (selectedObject: string) => {
        if(!savedQuery ||
            !savedQuery.filters ||
            !savedQuery.filters.patentFilters) return;
        const newSavedQuery = savedQuery;

        setSelectedPatentDateOption(selectedObject);
        newSavedQuery.filters.patentFilters.patentDateType = patentDateOptions.indexOf(selectedObject);
        if(newSavedQuery.filters.patentFilters.patentDateType > 0) {
            newSavedQuery.filters.patentFilters.patentDateType++;
        } 

        updateSavedQuery(newSavedQuery);
        await SavedQueryControllerSingleton.updateFilters(newSavedQuery);
    };

    const onChangeStartYear = async (newStartYear: string) => {
        if(!savedQuery ||
            !savedQuery.filters) return;
        const newSavedQuery = savedQuery;

        if(selectedSearchQueryType === SearchQueryTypeEnum.ScienceArticle) {
            if(!savedQuery.filters.scienceFilters) { return; }
            newSavedQuery.filters.scienceFilters.startDate = newStartYear;
        } else if(selectedSearchQueryType === SearchQueryTypeEnum.Patent) {
            if(!savedQuery.filters.patentFilters) { return; }
            newSavedQuery.filters.patentFilters.startDate = newStartYear;
        }

        updateSavedQuery(newSavedQuery);
        await SavedQueryControllerSingleton.updateFilters(newSavedQuery);
    };

    const onChangeEndYear = async (newEndYear: string) => {
        if(!savedQuery ||
            !savedQuery.filters) return;
        const newSavedQuery = savedQuery;

        if(selectedSearchQueryType === SearchQueryTypeEnum.ScienceArticle) {
            if(!savedQuery.filters.scienceFilters) { return; }
            newSavedQuery.filters.scienceFilters.endDate = newEndYear;
        } else if(selectedSearchQueryType === SearchQueryTypeEnum.Patent) {
            if(!savedQuery.filters.patentFilters) { return; }
            newSavedQuery.filters.patentFilters.endDate = newEndYear;
        }
        updateSavedQuery(newSavedQuery);
        await SavedQueryControllerSingleton.updateFilters(newSavedQuery);
    };

    // Country handlers
    const onAddCountry = async () => {
        if(!newCountryTextboxRef.current) return;
        if(!savedQuery ||
            !savedQuery.filters ||
            !savedQuery.filters.patentFilters) return;
        const newSavedQuery = savedQuery;

        if(!newSavedQuery.filters.patentFilters.filteredCountries) newSavedQuery.filters.patentFilters.filteredCountries = [];
        newSavedQuery.filters.patentFilters.filteredCountries = [...newSavedQuery.filters.patentFilters.filteredCountries, newCountryTextboxRef.current.value];

        updateSavedQuery(newSavedQuery);
        newCountryTextboxRef.current.value = "";
        await SavedQueryControllerSingleton.updateFilters(newSavedQuery);
    };

    const onChangeCountry = async (index: number, newText: string) => {
        if(!savedQuery ||
            !savedQuery.filters ||
            !savedQuery.filters.patentFilters) return;
        const newSavedQuery = savedQuery;
        
        if(!newSavedQuery.filters.patentFilters.filteredCountries) return;

        newSavedQuery.filters.patentFilters.filteredCountries = newSavedQuery.filters.patentFilters.filteredCountries.map((country, currentIndex) => {
            if(currentIndex === index) {
                return newText;
            }
            return country;
        });

        updateSavedQuery(newSavedQuery);
        await SavedQueryControllerSingleton.updateFilters(newSavedQuery);
    };

    const onDeleteCountry = async (index: number) => {
        if(!savedQuery ||
            !savedQuery.filters ||
            !savedQuery.filters.patentFilters) return;
        const newSavedQuery = savedQuery;

        if(!newSavedQuery.filters.patentFilters.filteredCountries) return;
        newSavedQuery.filters.patentFilters.filteredCountries = newSavedQuery.filters.patentFilters.filteredCountries.filter((_, i) => i !== index);

        updateSavedQuery(newSavedQuery);
        await SavedQueryControllerSingleton.updateFilters(newSavedQuery);
    };

    const onCountryOptionChange = async (selectedObject: string) => {
        if(!savedQuery ||
            !savedQuery.filters ||
            !savedQuery.filters.patentFilters) return;
        const newSavedQuery = savedQuery;

        const selectedObjectIndex = patentCountryOptions.indexOf(selectedObject);
        setSelectedCountryOptionIndex(selectedObjectIndex);
        newSavedQuery.filters.patentFilters.countryFilterType = selectedObjectIndex;

        updateSavedQuery(newSavedQuery);
        await SavedQueryControllerSingleton.updateFilters(newSavedQuery);
    };

    // Technology prediction handler
    const onChangeTechnologyPredictionModel = async (selectedObject: string) => {
        if(!savedQuery || !savedQuery.filters || !savedQuery.filters.technologyFilters) return;
        const newSavedQuery = savedQuery;

        setSelectedTechnologyPredictionModel(selectedObject);
        newSavedQuery.filters.technologyFilters.modelType = selectedObject;

        updateSavedQuery(newSavedQuery);
        await SavedQueryControllerSingleton.updateFilters(newSavedQuery);
    };

    const yearOnBlurHandler = async (isEndYear: boolean): Promise<void> => {
        if(!savedQuery || !savedQuery.filters) return;
        
        if(selectedSearchQueryType === SearchQueryTypeEnum.Patent) {
            if(!savedQuery.filters.patentFilters) return;
            
            if(isEndYear) {
                if(!savedQuery.filters.patentFilters.endDate) return;
                savedQuery.filters.patentFilters.endDate = validateAndFillDateInput(savedQuery.filters.patentFilters.endDate);
            } else {
                if(!savedQuery.filters.patentFilters.startDate) return;
                savedQuery.filters.patentFilters.startDate = validateAndFillDateInput(savedQuery.filters.patentFilters.startDate);
            }

        } else if(selectedSearchQueryType === SearchQueryTypeEnum.ScienceArticle) {
            if(!savedQuery.filters.scienceFilters) return;

            if(isEndYear) {
                if(!savedQuery.filters.scienceFilters.endDate) return;
                savedQuery.filters.scienceFilters.endDate = validateAndFillDateInput(savedQuery.filters.scienceFilters.endDate);
            } else {
                if(!savedQuery.filters.scienceFilters.startDate) return;
                savedQuery.filters.scienceFilters.startDate = validateAndFillDateInput(savedQuery.filters.scienceFilters.startDate);
            }
        } else {
            return;
        }

        updateSavedQuery(savedQuery);
        await SavedQueryControllerSingleton.updateFilters(savedQuery);
    };

    const validateAndFillDateInput = (value: string): string => {
        const dateInputValue = value.replace(/[^0-9]\/- /g, "");
        const typedDateInputValue = new Date(dateInputValue);
        if (typedDateInputValue.toString() === INVALID_DATE_MESSAGE) {
            return "";
        } else {
            const day = `${(typedDateInputValue.getDate() < 10 ? "0" : "")}${typedDateInputValue.getDate()}`;
            const month = `${((typedDateInputValue.getMonth() + 1) < 10 ? "0" : "")}${(typedDateInputValue.getMonth() + 1)}`;
            return `${typedDateInputValue.getFullYear()}-${month}-${day}`;
        }
    };
    
    return (
        <>
            <div className={styles.userFlowDataList} style={{ marginBottom: "2rem" }}>
                <h5 className={styles.dataListTitle}>Document type</h5>
                <div className={styles.dataListItem}>
                    <label>
                        <input
                            type="radio"
                            value={SearchQueryTypeEnum.ScienceArticle}
                            checked={selectedSearchQueryType === SearchQueryTypeEnum.ScienceArticle}
                            onChange={() => handleSearchQueryTypeChange(SearchQueryTypeEnum.ScienceArticle)} />Science
                    </label>
                </div>
                <div className={styles.dataListItem}>
                    <label>
                        <input
                            type="radio"
                            value={SearchQueryTypeEnum.Patent}
                            checked={selectedSearchQueryType === SearchQueryTypeEnum.Patent}
                            onChange={() => handleSearchQueryTypeChange(SearchQueryTypeEnum.Patent)} />Patent
                    </label>
                </div>
            </div>

            <div className={styles.userFlowDataList}>
                <h5 className={styles.dataListTitle}>Filters</h5>
                {isCurrentUserFindest ?
                    <div className={styles.filterListItem}>
                        <div className={styles.filterListItemTitle}>
                            <FontAwesomeIcon icon={faRobot} />
                            <span>Technology prediction model</span>
                        </div>
                        <div className={styles.dataListItem}>
                            <DivDropdown objectList={technologyPredictionModels} newObjectSelected={onChangeTechnologyPredictionModel} selectedObjectIndex={technologyPredictionModels.indexOf(selectedTechnologyPredictionModel)} className={styles.divDropdownFilter} overridenStyle={styles}>
                                {technologyPredictionModels.map((model, index) => {
                                    return (
                                        <div style={{ textTransform: "capitalize" }} key={`technology-prediction-model-${index}`}>
                                            <span>{model}</span>
                                        </div>
                                    );
                                })}
                            </DivDropdown>
                        </div>
                    </div>
                    : null }

                <div className={styles.filterListItem}>
                    <div className={styles.filterListItemTitle}>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        <span>Date range</span>
                    </div>
                    {selectedSearchQueryType === SearchQueryTypeEnum.Patent ?
                        <div className={styles.dataListItem}>
                            <DivDropdown objectList={patentDateOptions} newObjectSelected={onPatentDateTimeChange} selectedObjectIndex={patentDateOptions.indexOf(selectedPatentDateOption)} className={styles.divDropdownFilter} overridenStyle={styles}>
                                {patentDateOptions.map((option, index) => {
                                    return (
                                        <div style={{ textTransform: "capitalize" }} key={`date-option-${index}`}>
                                            <span>{option}</span>
                                        </div>
                                    );
                                })}
                            </DivDropdown>
                        </div>
                        : null}
                    <div className={styles.dataListItem}>
                        <div className={styles.rangeContainer}>
                            {savedQuery && selectedSearchQueryType === SearchQueryTypeEnum.ScienceArticle ?
                                <FindestTextBox placeholder="YYYY-MM-DD" onChange={onChangeStartYear}
                                    value={savedQuery?.filters?.scienceFilters?.startDate ? savedQuery.filters.scienceFilters.startDate : undefined}
                                    onBlur={() => { yearOnBlurHandler(false); }} />
                                : null 
                            }
                            {savedQuery && selectedSearchQueryType === SearchQueryTypeEnum.Patent ?
                                <FindestTextBox placeholder="YYYY-MM-DD" onChange={onChangeStartYear}
                                    value={savedQuery?.filters?.patentFilters?.startDate ? savedQuery.filters.patentFilters.startDate : undefined}
                                    onBlur={() => { yearOnBlurHandler(false); }} />
                                : null 
                            }
                            <span>-</span>
                            {savedQuery && selectedSearchQueryType === SearchQueryTypeEnum.ScienceArticle ?
                                <FindestTextBox placeholder="YYYY-MM-DD" onChange={onChangeEndYear}
                                    value={savedQuery?.filters?.scienceFilters?.endDate ? savedQuery.filters.scienceFilters.endDate : undefined}
                                    onBlur={() => { yearOnBlurHandler(true); }} />
                                : null 
                            }
                            {savedQuery && selectedSearchQueryType === SearchQueryTypeEnum.Patent ?
                                <FindestTextBox placeholder="YYYY-MM-DD" onChange={onChangeEndYear}
                                    value={savedQuery?.filters?.patentFilters?.endDate ? savedQuery.filters.patentFilters.endDate : undefined}
                                    onBlur={() => { yearOnBlurHandler(true); }} />
                                : null 
                            }
                        </div>
                    </div>
                </div>

                {savedQuery && isCurrentUserFindest && selectedSearchQueryType === SearchQueryTypeEnum.ScienceArticle &&
                        savedQuery.filters && savedQuery.filters.scienceFilters && savedQuery.filters.scienceFilters.documentTypesEnabled ?
                    <div className={styles.filterListItem}>
                        <div className={styles.filterListItemTitle}>
                            <FontAwesomeIcon icon={faFile} />
                            <span>Document Type</span>
                        </div>
                        <div className={styles.scienceDocumentTypeFilterContainer}>
                            <div className={styles.scienceDocumentTypeFilter}>
                                <input onChange={onChangeScienceDocumentType} value={ScienceDocumentTypeEnum.Journal} type="checkbox" 
                                    checked={savedQuery.filters.scienceFilters.documentTypesEnabled[ScienceDocumentTypeEnum.Journal]} 
                                    id="Journal" /> 
                                <label htmlFor="Journal">Journal</label>
                            </div>
                            <div className={styles.scienceDocumentTypeFilter}>
                                <input onChange={onChangeScienceDocumentType} value={ScienceDocumentTypeEnum.Conference} type="checkbox" 
                                    checked={savedQuery.filters.scienceFilters.documentTypesEnabled[ScienceDocumentTypeEnum.Conference]} 
                                    id="Conference" />
                                <label htmlFor="Conference">Conference</label>
                            </div>
                            <div className={styles.scienceDocumentTypeFilter}>
                                <input onChange={onChangeScienceDocumentType} value={ScienceDocumentTypeEnum.Book} type="checkbox" 
                                    checked={savedQuery.filters.scienceFilters.documentTypesEnabled[ScienceDocumentTypeEnum.Book]} 
                                    id="Book" />
                                <label htmlFor="Book">Book</label>
                            </div>
                            <div className={styles.scienceDocumentTypeFilter}>
                                <input onChange={onChangeScienceDocumentType} value={ScienceDocumentTypeEnum.BookChapter} type="checkbox" 
                                    checked={savedQuery.filters.scienceFilters.documentTypesEnabled[ScienceDocumentTypeEnum.BookChapter]} 
                                    id="Book Chapter" />
                                <label htmlFor="Book Chapter">Book Chapter</label>
                            </div>
                            <div className={styles.scienceDocumentTypeFilter}>
                                <input onChange={onChangeScienceDocumentType} value={ScienceDocumentTypeEnum.Thesis} type="checkbox" 
                                    checked={savedQuery.filters.scienceFilters.documentTypesEnabled[ScienceDocumentTypeEnum.Thesis]} 
                                    id="Thesis" />
                                <label htmlFor="Thesis">Thesis</label>
                            </div>
                            <div className={styles.scienceDocumentTypeFilter}>
                                <input onChange={onChangeScienceDocumentType} value={ScienceDocumentTypeEnum.Repository} type="checkbox" 
                                    checked={savedQuery.filters.scienceFilters.documentTypesEnabled[ScienceDocumentTypeEnum.Repository]} 
                                    id="Repository" />
                                <label htmlFor="Repository">Repository</label>
                            </div>
                            <div className={styles.scienceDocumentTypeFilter}>
                                <input onChange={onChangeScienceDocumentType} value={ScienceDocumentTypeEnum.Other} type="checkbox" 
                                    checked={savedQuery.filters.scienceFilters.documentTypesEnabled[ScienceDocumentTypeEnum.Other]} 
                                    id="Other" />
                                <label htmlFor="Other">Other</label>
                            </div>
                        </div>
                    </div>
                    : null}

                {isCurrentUserFindest ?
                    <div className={styles.filterListItem}>
                        <div className={styles.filterListItemTitle}>
                            <FontAwesomeIcon icon={faBuilding} />
                            <span>
                                {selectedSearchQueryType === SearchQueryTypeEnum.ScienceArticle ? "Affiliations" : "Assignees"}
                            </span>
                        </div>
                        <div className={[styles.dataListItem, styles.addDataListItem].join(" ")}>
                            <FindestRefTextBox placeholder={selectedSearchQueryType === SearchQueryTypeEnum.ScienceArticle ? "+ Affiliation" : "+ Assignee"} onEnter={onAddAffiliation} ref={newAffiliationTextboxRef} /> 
                            <button onClick={onAddAffiliation}>Add</button>
                        </div>
                        <div className={styles.addedDataListItemContainer}>
                            {savedQuery && selectedSearchQueryType === SearchQueryTypeEnum.Patent ?
                                <>
                                    {savedQuery?.filters?.patentFilters?.assignees?.map((assignee, index) => {
                                        return (
                                            <DeletableTextbox key={`patent-assignee-${index}`} text={assignee} index={index} 
                                                onChange={onChangeAffiliation} onDelete={onDeleteAffiliation} />
                                        );
                                    })}
                                </>
                                : null}
                            {savedQuery && selectedSearchQueryType === SearchQueryTypeEnum.ScienceArticle ?
                                <>
                                    {savedQuery?.filters?.scienceFilters?.affiliations?.map((affiliation, index) => {
                                        return (
                                            <DeletableTextbox key={`science-affiliation-${index}`} text={affiliation} index={index} 
                                                onChange={onChangeAffiliation} onDelete={onDeleteAffiliation} />
                                        );
                                    })}
                                </>
                                : null}
                        </div>
                    </div>
                    : null }

                {savedQuery && isCurrentUserFindest && selectedSearchQueryType === SearchQueryTypeEnum.Patent ?
                    <div className={styles.filterListItem}>
                        <div className={styles.filterListItemTitle}>
                            <FontAwesomeIcon icon={faGlobe} />
                            <span>Country</span>
                        </div>
                        <div className={styles.dataListItem}>
                            <DivDropdown objectList={patentCountryOptions} newObjectSelected={onCountryOptionChange} selectedObjectIndex={selectedCountryOptionIndex ?? 0} className={styles.divDropdownFilter} overridenStyle={styles}>
                                {patentCountryOptions.map((option, index) => {
                                    return (
                                        <div style={{ textTransform: "capitalize" }} key={`country-option-${index}`}>
                                            <span>{option}</span>
                                        </div>
                                    );
                                })}
                            </DivDropdown>
                        </div>
                        { selectedCountryOptionIndex !== 0 ?
                            <>
                                <div className={[styles.dataListItem, styles.addDataListItem].join(" ")}>
                                    <FindestRefTextBox placeholder="+ Country (e.g. US, EP, CN)" onEnter={onAddCountry} ref={newCountryTextboxRef} /> 
                                    <button onClick={onAddCountry}>Add</button>
                                </div>
                                <div className={styles.addedDataListItemContainer}>
                                    {savedQuery?.filters?.patentFilters?.filteredCountries?.map((country, index) => {
                                        return (
                                            <DeletableTextbox key={`country-${index}`} text={country} index={index} 
                                                onChange={onChangeCountry} onDelete={onDeleteCountry} />
                                        );
                                    })}
                                </div>
                            </>
                            : null}
                    </div>
                    : null}
            </div>
        </>
    );
};
